import './ServiceRequestMessageRoom.scss';

import React, {
  memo,
  useEffect,
  useMemo,
} from 'react';

import moment from 'moment';
import { FaFaceFrown } from 'react-icons/fa6';

import {
  ServiceRequestSocketAPI,
} from '../../../../api/ServiceRequestSocketAPI';
import CustomAvatar from '../../../../components/CustomAvatar/CustomAvatar';
import ProfileItem from '../../../../components/ProfileItem/ProfileItem';
import Loader from '../../../../components/V2/Loader/Loader';
import { useTheme } from '../../../../context/ThemeProvider';
import {
  clearSelectedServiceRequestMessageRoom,
  clearServiceRequestChatRoom,
  setSelectedServiceRequestMessageRoom,
} from '../../../../context/tqConnectServiceRequestReducer';
import {
  useTQConnectServiceRequestChatRoom,
} from '../../../../hooks/queries/tqConnectQueries/service-requests';
import {
  DATE_TIME_FORMAT,
  DEFAULT_AVATAR,
  DELETED_USER,
} from '../../../../shared/data';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import { checkIfCurrentLoggedInUser } from '../../../../shared/utils';
import { ROUTES } from '../../../../types/routes';
import {
  IServiceRequestMessageRoom,
  ITQServiceRequestUser,
  TQServiceRequestStatusType,
} from '../../../../types/tqConnectTypes/service-requests';
import NoDataFound from '../../../Feed/components/NoDataFound/NoDataFound';
import MessageRoomChats from './MessageRoomChats';

interface Props {
  serviceRequestId: string;
  status: TQServiceRequestStatusType;
  requestedBy?: ITQServiceRequestUser;
  requestedTo?: ITQServiceRequestUser;
  isFooterActionsVisible?: boolean;
}

const ServiceRequestMessageRoom: React.FC<Props> = ({
  serviceRequestId,
  status,
  requestedBy,
  requestedTo,
  isFooterActionsVisible,
}) => {
  const navigate = useAppNavigate();

  const { colors: COLORS } = useTheme();

  const dispatch = useAppDispatch();

  const { selectedServiceRequestMessageRoom, serviceRequestChatRoom } =
    useAppSelector((state) => state.tqConnectServiceRequest);

  const participantToShow = useMemo(() => {
    return checkIfCurrentLoggedInUser(requestedBy?._id || '')
      ? requestedTo
      : requestedBy;
  }, [requestedBy, requestedTo]);

  const {
    data: serviceRequestMessageRoomDetails,
    isLoading,
    error,
  } = useTQConnectServiceRequestChatRoom(serviceRequestId);

  const lastMessageTime = useMemo(() => {
    if (error || isLoading) return null;

    return (
      selectedServiceRequestMessageRoom?.lastMessageTime ||
      serviceRequestMessageRoomDetails?.lastMessageTime
    );
  }, [
    error,
    isLoading,
    selectedServiceRequestMessageRoom?.lastMessageTime,
    serviceRequestMessageRoomDetails?.lastMessageTime,
  ]);

  const joinRoom = (roomId: string) => {
    ServiceRequestSocketAPI.emit('room.join', { room: roomId });
  };

  const goToUserProfile = (userId?: string) => {
    if (!userId) return;

    navigate(ROUTES.USER_PROFILE, { userId });
  };

  useEffect(() => {
    if (
      serviceRequestMessageRoomDetails?._id &&
      serviceRequestChatRoom.roomId !== serviceRequestMessageRoomDetails?._id
    ) {
      joinRoom(serviceRequestMessageRoomDetails?._id);
      // clear service request chat room
      dispatch(clearSelectedServiceRequestMessageRoom());
      dispatch(clearServiceRequestChatRoom());
      dispatch(
        setSelectedServiceRequestMessageRoom(serviceRequestMessageRoomDetails),
      );
    }
  }, [
    dispatch,
    serviceRequestId,
    serviceRequestMessageRoomDetails,
    serviceRequestChatRoom.roomId,
  ]);

  // useEffect(() => {
  //   // clear service request chat room
  //   dispatch(clearSelectedServiceRequestMessageRoom());
  //   dispatch(clearServiceRequestChatRoom());
  // }, [dispatch, serviceRequestId]);

  return (
    <div
      className="service-request-message-room"
      key={`service-request-message-room-${serviceRequestId}`}>
      <div className="message-room__header">
        <ProfileItem
          title={participantToShow?.name || DELETED_USER}
          subtitle={
            lastMessageTime
              ? `Last message on ${moment(lastMessageTime).format(DATE_TIME_FORMAT)}`
              : undefined
          }
          avatar={
            <CustomAvatar
              size={48}
              src={participantToShow?.profilePicUrl || DEFAULT_AVATAR}
              border={0}
              bgColor={COLORS.BACKGROUND}
            />
          }
          avatarTitle={participantToShow?.name || DELETED_USER}
          onClick={() => {
            goToUserProfile(participantToShow?._id);
          }}
          titleStyle={{
            fontSize: 20,
          }}
          style={{ cursor: 'pointer' }}
        />
      </div>

      {error ? (
        <NoDataFound
          iconElement={<FaFaceFrown size={96} color={COLORS.TEXT4} />}
          title="Oops! Unable to load messages"
          subtitle="Maybe the messages are not available anymore or please try again later."
          style={{
            width: '100%',
            padding: 16,
            paddingTop: 48,
          }}
        />
      ) : null}

      {(isLoading ||
        !serviceRequestMessageRoomDetails ||
        serviceRequestMessageRoomDetails?._id !==
          serviceRequestChatRoom.roomId) &&
      !error ? (
        <Loader />
      ) : (
        <MessageRoomChats
          key={`service-request-message-room-chats-${serviceRequestMessageRoomDetails?._id}`}
          roomId={serviceRequestMessageRoomDetails?._id || ''}
          serviceRequestId={serviceRequestId}
          status={status}
          serviceRequestMessageRoomDetails={
            serviceRequestMessageRoomDetails as IServiceRequestMessageRoom
          }
          isFooterActionsVisible={isFooterActionsVisible}
        />
      )}
    </div>
  );
};

export default memo(ServiceRequestMessageRoom);
